import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import { useMediaQuery } from "@mui/material";
import { formattedIframeByType } from "../../../../services/adsCampaigns";
import { startGetAdPreview } from "../../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../../lib/redux";
import { META } from "../../../../utils/constants";
import { useFormContext } from "react-hook-form";
import _ from "lodash";
import { useDebounced } from "../../../../hooks/useDebounce";

const PlacementPreview = ({
  adFormat,
  customizationPlacements,
  creativeID,
  destinationType,
  adID,
  adPathForm,
  index,
  onCallbackGeneratedAdPreview = () => {},
  isLoadingPreview = false,
}) => {
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [error, setError] = useState("");
  const [hasPreviousImage, setHasPreviousImage] = useState(false);
  const { campaignID, getValues, previewsCached } = useFormContext();

  const platformData = getValues(`platformsData.${META}`) || {};
  const isIOS14Campaign = platformData?.isIOS14Campaign;
  const integrationID = platformData?.integrationID;
  const adAccountID = platformData?.adAccountID;
  const pageID = platformData?.pageID;
  const instagramID = platformData?.instagramID;
  const useFBPageAsIG = platformData?.useFBPageAsIG || false;

  const adsGroups = getValues(`adsGroups`) || [];
  const adGroupID = getValues(`${adPathForm}.adGroupID`);
  const adGroup = adsGroups.find((adGroup) => adGroup.id === adGroupID);
  const appID = adGroup?.appID;
  const titles = getValues(`${adPathForm}.titles`);
  const descriptions = getValues(`${adPathForm}.descriptions`);
  const headlines = getValues(`${adPathForm}.headlines`);
  const callToAction = getValues(`${adPathForm}.callToAction`);
  const destinationUrls = getValues(`${adPathForm}.destinationUrls`);
  const mediaUrls = getValues(`${adPathForm}.mediaUrls`);
  const mediaIDs = getValues(`${adPathForm}.mediaIDs`);
  const mediaData = getValues(`${adPathForm}.mediaData`);
  const destinationTypeAd = getValues(`${adPathForm}.destinationType`);
  const address = getValues(`${adPathForm}.address`);
  const id = getValues(`${adPathForm}.id`);

  const [iFrameSrc, setIFrameSrc] = useState({
    previous: "",
    current: "",
  });
  const [previousAdFormat, setPreviousAdFormat] = useState(adFormat);
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const isMobile = useMediaQuery(() => "(max-width: 360px)");

  const dispatch = useDispatchApp();
  const isCarousel = getValues(`${adPathForm}.isCarousel`);

  const onChangeAdPreview = (value) => {
    setPreviousAdFormat(adFormat);
    setIFrameSrc((prev) => ({
      previous: prev.current,
      current: value,
    }));
  };

  const onGeneratePreview = useDebounced(async () => {
    setError(null);

    const mediaDataCopy = _.cloneDeep(mediaData);
    if (!mediaDataCopy[index]) {
      mediaDataCopy[index] = {
        from: "upload",
      };
    }
    mediaDataCopy[index].customizationPlacements = customizationPlacements;
    const response = await dispatch(
      startGetAdPreview({
        platform: META,
        campaignID,
        adID,
        mediaUrls,
        mediaIDs,
        mediaData: mediaDataCopy,
        callToAction,
        titles: titles ? titles.map((title) => title.value) : [],
        headlines: headlines ? headlines.map((headline) => headline.value) : [],
        descriptions: descriptions
          ? descriptions.map((description) => description.value)
          : [],
        destinationUrls: destinationUrls
          ? destinationUrls.map((url) => url.value)
          : [],
        pageID,
        instagramID,
        useFBPageAsIG,
        adFormats: [adFormat],
        platformData: {
          integrationID,
          adAccountID,
          pageID,
          instagramID,
          useFBPageAsIG,
        },
        destinationType,
        destinationTypeAd,
        isCarousel: false,
        from: "placementPreview",
        creativeID,
        appID,
        isIOS14Campaign,
        address,
      })
    );
    if (!response.ok) {
      setError(response.message);
    }
    if (response.ok) {
      const {
        adPreviews: adPreviewsResponse,
        creativeID: creativeIDResponse,
        mediaData: mediaDataResponse,
      } = response.data;
      onChangeAdPreview(adPreviewsResponse[adFormat]);
      onCallbackGeneratedAdPreview({
        creativeID: creativeIDResponse,
        mediaData: mediaDataResponse,
      });
    }
    // setIsLoadingIframe(true);
  }, [1000]);

  useEffect(() => {
    if (!isFirstLoad) return;
    onGeneratePreview();
    // eslint-disable-next-line
  }, [customizationPlacements, adFormat]);

  useEffect(() => {
    if (previewsCached?.[id]?.[adFormat]) {
      onChangeAdPreview(previewsCached[id][adFormat]);
      setIsLoadingIframe(true);
    } else {
      onGeneratePreview();
    }
    setIsFirstLoad(true);
    return;
    // eslint-disable-next-line
  }, []);

  const onGetInformationIframe = () => {
    const { height, width, src } = formattedIframeByType({
      type: adFormat,
      iframe: iFrameSrc?.current || "",
      isMobile,
      isCarousel,
      platform: META,
    });
    const {
      height: heightPrevious,
      width: widthPrevious,
      src: srcPrevious,
    } = formattedIframeByType({
      type: previousAdFormat,
      iframe: iFrameSrc?.previous || "",
      isMobile,
      isCarousel,
      platform: META,
    });

    if (isLoadingIframe && !srcPrevious) {
      return {
        height: height,
        width: width,
        src: src,
        srcPrevious: src,
      };
    }

    if (isLoadingIframe && srcPrevious) {
      return {
        height: heightPrevious,
        width: widthPrevious,
        src: src,
        srcPrevious: srcPrevious || src,
      };
    }

    return {
      height,
      width,
      src,
      srcPrevious,
    };
  };

  // Using useMemo to recalculate values only when isLoadingIframe changes
  const iframeInfo = React.useMemo(() => {
    return onGetInformationIframe();
    // eslint-disable-next-line
  }, [isLoadingIframe, iFrameSrc, isMobile]);

  const { height, width, src, srcPrevious } = iframeInfo;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      {error && (
        <Typography variant="body1" color="error" align="center">
          {error}
        </Typography>
      )}
      {!error && (
        <Box
          sx={{
            width: width,
            height,
            position: "relative",
          }}
        >
          {isLoadingIframe && !src && !srcPrevious && (
            <Skeleton
              variant="rectangular"
              sx={{
                width: width,
                height,
              }}
            />
          )}

          {srcPrevious && (
            <iframe
              src={srcPrevious}
              scrolling="no"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                border: isMobile ? "none" : "1px solid white",
                overflow: "hidden",
                maxWidth: width,
                width: "100%",
                height,
                zIndex: isLoadingIframe || src ? 2 : 1,
              }}
            />
          )}

          {src && (
            <iframe
              src={src}
              onLoad={() => {
                setIsLoadingIframe(false);
                setHasPreviousImage(true);
              }}
              scrolling="no"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                display: isLoadingIframe ? "block" : "block",
                border: isMobile ? "none" : "1px solid white",
                overflow: "hidden",
                maxWidth: width,
                width: "100%",
                height,
                zIndex: isLoadingIframe ? 1 : 2,
              }}
            />
          )}

          {/* Loader para nueva imagen */}
          {(isLoadingPreview || isLoadingIframe) && hasPreviousImage && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 3,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PlacementPreview;
